// Aug 2026 - shared color logic for the System Banner feature (SystemBannerDrawer's admin picker
// and the live SystemBanner display both import this, so they can never drift apart).
//
// A banner's persisted `color` is a plain string, one of two shapes:
//   - a preset name ("Teal" / "Green" / "Red") - the original 3 subtle pastel themes, kept
//     hardcoded pixel-for-pixel so existing banners never visually shift.
//   - a "#RRGGBB" hex string - picked via the free-form custom color picker added Aug 2026. Its
//     bg/border/text tint is derived algorithmically from the picked hex (see mix() below) rather
//     than hardcoded, since it can be any color an admin chooses.
export type BannerPalette = { bg: string; border: string; text: string; icon: string };

export type BannerColorPreset = { value: string; label: string; palette: BannerPalette };

export const BANNER_COLOR_PRESETS: BannerColorPreset[] = [
    { value: 'Teal', label: 'Teal (default)', palette: { bg: '#ECFEFF', border: '#A5F3FC', text: '#155E63', icon: '#0891B2' } },
    { value: 'Green', label: 'Green', palette: { bg: '#ECFDF5', border: '#A7F3D0', text: '#065F46', icon: '#059669' } },
    { value: 'Red', label: 'Red', palette: { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', icon: '#DC2626' } },
];

export const DEFAULT_BANNER_PALETTE = BANNER_COLOR_PRESETS[0].palette;

export const isHexColor = (value: string | undefined): value is string =>
    !!value && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value);

// Expands "#abc" to "#aabbcc" so downstream math can always assume 6 digits.
const normalizeHex = (hex: string): string => {
    if (hex.length === 4) {
        return '#' + hex.slice(1).split('').map(c => c + c).join('');
    }
    return hex;
};

const hexToRgb = (hex: string): [number, number, number] => {
    const normalized = normalizeHex(hex);
    const num = parseInt(normalized.slice(1), 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
};

const rgbToHex = (r: number, g: number, b: number): string =>
    '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');

// Blends `hex` toward `toward` by `weight` (0 = pure hex, 1 = pure toward).
const mix = (hex: string, toward: [number, number, number], weight: number): string => {
    const [r, g, b] = hexToRgb(hex);
    return rgbToHex(
        r + (toward[0] - r) * weight,
        g + (toward[1] - g) * weight,
        b + (toward[2] - b) * weight,
    );
};

// Derives a subtle-pastel banner palette from an arbitrary hex color: a near-white tinted
// background, a light tinted border a bit stronger than the background, and a darkened version of
// the same hue for readable text - matching the same "very subtle" feel as the hardcoded presets,
// but for a color an admin picked freely rather than one of the 3 fixed choices.
const deriveFromHex = (hex: string): BannerPalette => ({
    bg: mix(hex, [255, 255, 255], 0.94),
    border: mix(hex, [255, 255, 255], 0.72),
    text: mix(hex, [0, 0, 0], 0.45),
    icon: normalizeHex(hex),
});

export const getBannerPalette = (color: string | undefined): BannerPalette => {
    const preset = BANNER_COLOR_PRESETS.find(p => p.value === color);
    if (preset) return preset.palette;
    if (isHexColor(color)) return deriveFromHex(color);
    return DEFAULT_BANNER_PALETTE;
};
